import React, { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { SettingsCard, SettingsCards, SettingsFooter, SettingsPageHeader } from '../SettingsShell/SettingsPage';
import { SetSelect, SetSwitch } from '../SettingsShell/controls';
import { KB_CATEGORIES, KB_CATEGORY_LABEL } from '../../settings-brain/types';
import type { KbCategory, KnowledgeBaseData, KnowledgeEntry } from '../../settings-brain/types';

export interface KnowledgeBaseViewProps {
  data: KnowledgeBaseData;
  location: string;
  onOpenTrainElva?: () => void;
  onOpenPracticeManagement?: () => void;
  /** ?question= — opens the editor empty, the question as *also ask* + a suggested title. */
  prefillQuestion?: string;
  /** ?body= — opens the editor with the text in *What ELVA should say* (Move to KB, §6). */
  prefillBody?: string;
  /** ?title= — the title for a new entry (e.g. "First missed visit"). */
  prefillTitle?: string;
  /** ?entry= — opens the editor on the entry whose title matches. */
  prefillEntry?: string;
  /** ?category= — pre-selects the category on a new entry (from Train's Move to KB). */
  prefillCategory?: KbCategory;
}

interface Draft {
  id: string | null;
  title: string;
  category: KbCategory;
  body: string;
  alsoAsk: string;
  enabled: boolean;
}

const emptyDraft = (category: KbCategory = 'faq'): Draft => ({ id: null, title: '', category, body: '', alsoAsk: '', enabled: true });

/**
 * AI Brain › Knowledge base — add, edit, practice facts (amendment 32 §4). A blue
 * strip links unanswered questions to Train ELVA; a read-only block shows the
 * facts Practice management owns; entries open an editor on the right with add,
 * edit, switch-off and delete, and a locked-sentence warning.
 */
export function KnowledgeBaseView({ data, location, onOpenTrainElva, onOpenPracticeManagement, prefillQuestion, prefillBody, prefillTitle, prefillEntry, prefillCategory }: KnowledgeBaseViewProps) {
  const [entries, setEntries] = useState(data.entries);
  const [category, setCategory] = useState<'all' | KbCategory>('all');
  const [draft, setDraft] = useState<Draft | null>(() => {
    if (prefillEntry !== undefined) {
      const found = data.entries.find((e) => e.title.toLowerCase() === prefillEntry.toLowerCase());
      if (found) return { id: found.id, title: found.title, category: found.category, body: found.body, alsoAsk: found.alsoAsk ?? '', enabled: found.enabled };
    }
    if (prefillBody !== undefined) return { ...emptyDraft(prefillCategory), title: prefillTitle ?? '', body: prefillBody };
    if (prefillQuestion !== undefined) return { ...emptyDraft(prefillCategory), title: prefillQuestion, alsoAsk: prefillQuestion };
    return null;
  });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const catCount = (c: KbCategory) => entries.filter((e) => e.category === c).length;
  const usedCategories = KB_CATEGORIES.filter((c) => catCount(c.id) > 0);
  const filtered = useMemo(() => (category === 'all' ? entries : entries.filter((e) => e.category === category)), [entries, category]);

  const openEntry = (e: KnowledgeEntry) => { setConfirmDelete(false); setDraft({ id: e.id, title: e.title, category: e.category, body: e.body, alsoAsk: e.alsoAsk ?? '', enabled: e.enabled }); };
  const openNew = () => { setConfirmDelete(false); setDraft(emptyDraft()); };
  const save = () => {
    if (draft === null) return;
    if (draft.id !== null) {
      setEntries((p) => p.map((e) => (e.id === draft.id ? { ...e, title: draft.title, category: draft.category, body: draft.body, alsoAsk: draft.alsoAsk, enabled: draft.enabled, updatedAt: 'just now' } : e)));
    } else {
      setEntries((p) => [...p, { id: `k${String(Date.now())}`, title: draft.title, category: draft.category, body: draft.body, alsoAsk: draft.alsoAsk, enabled: draft.enabled, updatedAt: 'just now', usedCount: 0 }]);
    }
    setDraft(null);
  };
  const del = () => { if (draft?.id != null) setEntries((p) => p.filter((e) => e.id !== draft.id)); setDraft(null); setConfirmDelete(false); };
  const current = draft?.id != null ? entries.find((e) => e.id === draft.id) : undefined;

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[900px] flex-1 overflow-y-auto px-[30px] pb-16 pt-6">
      <SettingsPageHeader eyebrow="Settings · AI Brain" titleLead="Knowledge" titleAccent="base" sub="What ELVA knows about your practice, in your words. Every agent reads it." />

      {/* blue strip */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4 rounded-[12px] border border-[var(--brand-indigo-line)] bg-[var(--brand-indigo-tint)] px-5 py-3.5">
        <span className="text-[13px] text-[var(--text-secondary)]"><span className="font-bold text-[var(--brand-indigo)]">{data.couldNotAnswer} questions have no answer.</span> Work through them on Train ELVA — Write an entry opens here with the question filled in.</span>
        <button type="button" className="shrink-0 rounded-[8px] bg-[var(--brand-indigo)] px-3.5 py-2 text-[12.5px] font-semibold text-white" onClick={onOpenTrainElva}>Open Train ELVA →</button>
      </div>

      {/* From Practice management (read-only) */}
      <div className="mb-5 rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[13px] font-bold text-[var(--text-primary)]">From Practice management</div>
            <div className="text-[11.5px] text-[var(--text-muted)]">Used by every agent. Change them there.</div>
          </div>
          <button type="button" className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={onOpenPracticeManagement}>Practice management →</button>
        </div>
        <div className="mt-3 grid grid-cols-4 gap-3 max-[720px]:grid-cols-2 text-[12.5px]">
          <Fact label="Office hours" value={data.practiceFacts.officeHours} />
          <Fact label="Address" value={data.practiceFacts.address} />
          <Fact label="Phone" value={data.practiceFacts.phone} />
          <Fact label="Doctors" value={data.practiceFacts.doctors} />
        </div>
      </div>

      <div className={cn('grid gap-4', draft !== null ? 'grid-cols-[1fr_360px] max-[900px]:grid-cols-1' : 'grid-cols-1')}>
        <SettingsCards>
          <SettingsCard n="01" title="Entries" sub={`${String(entries.length)} entries. Anyone can add one.`} headRight={<Button variant="primary" size="sm" iconLeft="plus" onClick={openNew}>Add an entry</Button>}>
            <div className="flex flex-wrap items-center gap-2 px-6 pt-4">
              <Chip on={category === 'all'} onClick={() => { setCategory('all'); }}>All <span className="ml-1 text-[var(--text-muted)]">{entries.length}</span></Chip>
              {usedCategories.map((c) => (
                <Chip key={c.id} on={category === c.id} onClick={() => { setCategory(c.id); }}>{c.label} <span className="ml-1 text-[var(--text-muted)]">{catCount(c.id)}</span></Chip>
              ))}
            </div>
            <div className="mt-3">
              {filtered.map((e) => (
                <EntryRow key={e.id} e={e} active={draft?.id === e.id} onOpen={() => { openEntry(e); }} onToggle={(next) => { setEntries((p) => p.map((x) => (x.id === e.id ? { ...x, enabled: next } : x))); }} />
              ))}
            </div>
          </SettingsCard>
        </SettingsCards>

        {draft !== null ? (
          <aside className="h-max rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]">
            <div className="flex items-center justify-between">
              <div className="text-[13px] font-bold text-[var(--text-primary)]">{draft.id != null ? draft.title || 'Entry' : 'New entry'}</div>
              <button type="button" aria-label="Close" className="text-[18px] leading-none text-[var(--text-muted)] hover:text-[var(--text-primary)]" onClick={() => { setDraft(null); }}>×</button>
            </div>
            <Field label="Title"><input value={draft.title} onChange={(e) => { setDraft({ ...draft, title: e.target.value }); }} placeholder="Ask it the way a patient would…" className="kb-in" /></Field>
            <Field label="Category"><SetSelect value={draft.category} options={KB_CATEGORIES.map((c) => ({ value: c.id, label: c.label }))} onChange={(v) => { setDraft({ ...draft, category: v }); }} ariaLabel="Category" /></Field>
            <Field label="What ELVA should say"><textarea value={draft.body} onChange={(e) => { setDraft({ ...draft, body: e.target.value }); }} className="kb-in h-[80px] resize-none" /></Field>
            {current?.lockedSentence !== undefined ? (
              <div className="mt-2 rounded-[8px] border border-[color-mix(in_srgb,var(--warn-amber)_30%,transparent)] bg-[var(--warn-amber-fill)] px-3 py-2 text-[11.5px] text-[var(--text-secondary)]">
                <span className="font-semibold text-[var(--warn-amber)]">ELVA won’t say “{current.lockedSentence}”.</span> {current.lockedNote}
              </div>
            ) : null}
            <Field label="Patients also ask it as" hint="Optional. Helps ELVA find this entry."><input value={draft.alsoAsk} onChange={(e) => { setDraft({ ...draft, alsoAsk: e.target.value }); }} className="kb-in" /></Field>
            <div className="mt-3 flex items-center gap-2">
              <SetSwitch checked={draft.enabled} onChange={(v) => { setDraft({ ...draft, enabled: v }); }} label="Active" />
              <span className="text-[12px] text-[var(--text-secondary)]">Active</span>
            </div>
            {current !== undefined ? <div className="mt-2 text-[11px] text-[var(--text-muted)]">Last changed {current.updatedAt} · used {String(current.usedCount ?? 0)} times</div> : null}
            <div className="mt-3 flex items-center gap-2">
              {draft.id != null ? <button type="button" className="text-[12px] font-semibold text-[var(--alert-red)] hover:underline" onClick={() => { setConfirmDelete(true); }}>Delete</button> : null}
              <div className="ml-auto flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => { setDraft(null); }}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={save}>Save</Button>
              </div>
            </div>
            {confirmDelete ? (
              <div className="mt-2 flex items-center gap-2 rounded-[8px] bg-[var(--surface-sunken)] px-3 py-2 text-[12px]">
                <span className="mr-auto text-[var(--text-secondary)]">Delete this entry?</span>
                <Button variant="secondary" size="sm" onClick={() => { setConfirmDelete(false); }}>Cancel</Button>
                <Button variant="danger" size="sm" onClick={del}>Delete</Button>
              </div>
            ) : null}
            <style>{`.kb-in{width:100%;border:1px solid var(--border-default);border-radius:8px;padding:7px 10px;font-size:13px;color:var(--text-primary);outline:none;background:var(--surface-card)}.kb-in:focus{border-color:var(--brand-indigo)}`}</style>
          </aside>
        ) : null}
      </div>

      <SettingsFooter location={location} />
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div><div className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-muted)]">{label}</div><div className="mt-0.5 font-semibold text-[var(--text-primary)]">{value}</div></div>;
}
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return <div className="mt-3"><div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--text-muted)]">{label}</div><div className="mt-1">{children}</div>{hint !== undefined ? <div className="mt-1 text-[10.5px] text-[var(--text-muted)]">{hint}</div> : null}</div>;
}
function Chip({ children, on, onClick }: { children: React.ReactNode; on: boolean; onClick: () => void }) {
  return <button type="button" onClick={onClick} className={cn('rounded-full border px-3 py-1 text-[12px] font-semibold', on ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]' : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]')}>{children}</button>;
}
function EntryRow({ e, active, onOpen, onToggle }: { e: KnowledgeEntry; active: boolean; onOpen: () => void; onToggle: (next: boolean) => void }) {
  return (
    <div className={cn('flex items-start justify-between gap-4 px-6 py-3', active ? 'bg-[var(--brand-indigo-tint)]' : '')} style={{ borderTop: '1px solid #edf0f6' }}>
      <button type="button" className="min-w-0 flex-1 text-left" onClick={onOpen}>
        <span className="flex flex-wrap items-baseline gap-2">
          <span className={cn('text-[13px] font-semibold', e.enabled ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]')}>{e.title}</span>
          <span className="rounded-full border border-[var(--border-default)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--text-muted)]">{KB_CATEGORY_LABEL[e.category]}</span>
          {e.lockedSentence !== undefined ? <span className="rounded-full bg-[var(--warn-amber-fill)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--warn-amber)]">1 sentence not used</span> : null}
        </span>
        <span className="mt-0.5 block text-[12px] leading-[1.5] text-[var(--text-muted)]">{e.body}</span>
        <span className="mt-0.5 block text-[10.5px] text-[var(--text-muted)]">Used {String(e.usedCount ?? 0)} times · 30 days</span>
      </button>
      <span className="flex shrink-0 items-center gap-3">
        <SetSwitch label={`${e.title} active`} checked={e.enabled} onChange={onToggle} />
        <button type="button" aria-label="Edit" className="text-[var(--text-muted)] hover:text-[var(--brand-indigo)]" onClick={onOpen}><Icon kind="pencil" size={14} stroke={2} /></button>
      </span>
    </div>
  );
}

export { KB_CATEGORY_LABEL };
